import { Component, OnDestroy, OnInit } from '@angular/core';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { RequestAPIService } from 'src/app/extensions/services/api/request-api.service';
import { ScrollService } from 'src/app/extensions/services/scroll.service';
import { RequestListFilterComponent } from './request-list-filter/request-list-filter.component';
import { RequestListPartnerFilterComponent } from './request-list-partner-filter/request-list-partner-filter.component';
import { Router } from '@angular/router';
import { DictionaryAPIService } from 'src/app/extensions/services/api/dictionary-api.service';
import { RESPONSIVE_BREAKPOINTS } from 'src/app/extensions/constants/responsive-breakpoints';
import { WindowEventsService } from 'src/app/extensions/services/window-events.services';
import { Subscription } from 'rxjs';
import { scrollTop, setTableViewMode } from 'src/app/extensions/utilities';
import { ADMIN_REQUEST_ROUTES } from '../requests-routing.module';
import {
  DICTIONARY_REQUEST_TYPES,
  FILTERS_RESET_TYPE,
  TABLE_VIEW_MODES,
} from 'src/app/extensions/constants/commonConstants';
import { GlobalService } from 'src/app/extensions/services/global.service';
import { isEqual } from 'lodash';

@Component({
  selector: 'app-request-list',
  templateUrl: './request-list.component.html',
  styleUrls: ['./request-list.component.scss'],
  providers: [ScrollService],
  standalone: false,
})
export class RequestListComponent implements OnInit, OnDestroy {
  public requests: app.models.RequestListModel[];
  public props: app.models.DynamicPropsDictionary;
  public dictionaryRequestTypes: app.models.DictionaryRequestType[] = [];

  public FILTERS_RESET_TYPE = FILTERS_RESET_TYPE;
  private pageIndex = 1;
  private maxIndex: number;

  public activeFilterCount = 0;
  public activePartnerFilterCount = 0;
  public hasFilteredData: boolean = true;
  public loading: boolean = false;

  public showBackToTop: boolean = false;

  public filters: app.models.GetRequestsForAdministrationModelPageFilter;
  public initialFilterState: app.models.GetRequestsForAdministrationModelPageFilter;
  public subscriptions: Subscription[] = [];
  public TABLE_VIEW_MODES = TABLE_VIEW_MODES;
  public tableViewMode: string;
  windowSizeLgUp: boolean;
  public currentRoute: string;

  public constructor(
    private _globalService: GlobalService,
    private _scrollService: ScrollService,
    private _requestAPIService: RequestAPIService,
    private _ngbModal: NgbModal,
    private _router: Router,
    private _dictionaryAPIService: DictionaryAPIService,
    private _windowEventService: WindowEventsService
  ) {
    this.currentRoute = this._router.url.split('/')[1];
  }

  public ngOnInit(): void {
    this.tableViewMode = setTableViewMode(this._globalService, this.currentRoute);

    this.filters = {
      startDate: null,
      endDate: null,
      partners: [],
      requestTypeId: '',
      adminUser: null,
    };

    this.initialFilterState = structuredClone(this.filters);
    this._dictionaryAPIService.getDictionaryRequestType().subscribe(result => {
      this.dictionaryRequestTypes = result.map(x => {
        return {
          ...x,
          redirectRoute: `client-requests/detail/${
            x.dictionaryRequestTypeCode == DICTIONARY_REQUEST_TYPES.ASSET_DOCUMENT
              ? ADMIN_REQUEST_ROUTES.ASSET_DOCUMENT
              : x.dictionaryRequestTypeCode == DICTIONARY_REQUEST_TYPES.CONTRACT_MODIFICATION
                ? ADMIN_REQUEST_ROUTES.CONTRACT_MODIFICATIONS
                : x.dictionaryRequestTypeCode == DICTIONARY_REQUEST_TYPES.CLIENT_DATA
                  ? ADMIN_REQUEST_ROUTES.CLIENT_DATA
                  : undefined
          }`,
        };
      });
    });

    this.getPagedRequests(this.pageIndex, this.filters);

    let backToTopSubscription = this._scrollService.obsShowBackToTop.subscribe(x => {
      this.showBackToTop = x;
    });
    this.subscriptions.push(backToTopSubscription);

    let subscription = this._scrollService.obsFetchData.subscribe(() => {
      if (this.pageIndex <= this.maxIndex) {
        this.pageIndex++;
        this._requestAPIService.getPagedRequestsForAdministration(this.pageIndex, this.filters).subscribe(x => {
          this.requests = this.requests.concat(x.data.values);
          if (this.pageIndex <= this.maxIndex) {
            this._scrollService.waitingForDataFetch = false;
          }
        });
      }
    });
    this.subscriptions.push(subscription);
    this.windowSizeLgUp = window.innerWidth >= RESPONSIVE_BREAKPOINTS.lg;
    subscription = this._windowEventService.onResize.subscribe(() => {
      this.windowSizeLgUp = window.innerWidth >= RESPONSIVE_BREAKPOINTS.lg;
    });
    this.subscriptions.push(subscription);
  }

  ngOnDestroy(): void {
    if (this.subscriptions) {
      this.subscriptions.forEach(subscription => {
        subscription.unsubscribe();
      });
    }
  }

  public getPagedRequests(pageIndex: number, filters: app.models.GetRequestsForAdministrationModelPageFilter) {
    this._requestAPIService.getPagedRequestsForAdministration(pageIndex, filters).subscribe(x => {
      this.requests = x.data.values;
      this.props = x.props;

      this.maxIndex = x.data.totalPages;
      this.hasFilteredData = this.requests.length > 0;

      this._scrollService.waitingForDataFetch = this.pageIndex > this.maxIndex;
    });
  }

  public resetFiltersHandler(resetType: string = FILTERS_RESET_TYPE.TOTAL) {
    if ([FILTERS_RESET_TYPE.OTHER_FILTERS, FILTERS_RESET_TYPE.TOTAL].includes(resetType)) {
      this.activePartnerFilterCount = 0;
      this.filters.partners = [];
    }
    if ([FILTERS_RESET_TYPE.ACTIVE_FILTERS, FILTERS_RESET_TYPE.TOTAL].includes(resetType)) {
      this.activeFilterCount = 0;
      this.filters.startDate = null;
      this.filters.startDate = null;
      this.filters.requestTypeId = '';
    }

    this.initialFilterState = {
      ...this.initialFilterState,
      partners: this.filters.partners,
      startDate: this.filters.startDate,
      endDate: this.filters.endDate,
      requestTypeId: this.filters.requestTypeId,
      adminUser: this.filters.adminUser,
    };

    if (!isEqual(this.filters, this.initialFilterState)) {
      this.filters = structuredClone(this.initialFilterState);
    }

    this.pageIndex = 1;
    this.filters = {} as app.models.GetRequestsForAdministrationModelPageFilter;
    this.getPagedRequests(this.pageIndex, this.filters);
  }

  public filterHandler() {
    let ngbModalOptions: NgbModalOptions = {
      backdrop: 'static',
      scrollable: true,
    };

    let instance = this._ngbModal.open(RequestListFilterComponent, ngbModalOptions);
    instance.componentInstance.requestModalFilters = this.filters;
    instance.componentInstance.initialFilterState = this.initialFilterState;

    instance.closed.subscribe(result => {
      if (result !== false) {
        this.initialFilterState = {
          ...this.initialFilterState,
          startDate: this.filters.startDate,
          endDate: this.filters.endDate,
          requestTypeId: this.filters.requestTypeId,
          adminUser: this.filters.adminUser,
        };
        this.activeFilterCount = result;
        this.pageIndex = 1;
        if (!isEqual(this.filters, this.initialFilterState)) {
          this.filters = structuredClone(this.initialFilterState);
        }
        this.getPagedRequests(this.pageIndex, this.filters);
      }
    });
  }

  public partnerFilterHandler() {
    let ngbModalOptions: NgbModalOptions = {
      backdrop: 'static',
    };

    let instance = this._ngbModal.open(RequestListPartnerFilterComponent, ngbModalOptions);
    instance.componentInstance.requestModalFilters = this.filters;
    instance.componentInstance.initialFilterState = this.initialFilterState;

    instance.closed.subscribe(result => {
      if (result !== false) {
        this.initialFilterState = {
          ...this.initialFilterState,
          partners: this.filters.partners,
        };
        this.pageIndex = 1;
        this.activePartnerFilterCount = result;
        if (!isEqual(this.filters, this.initialFilterState)) {
          this.filters = structuredClone(this.initialFilterState);
        }
        this.getPagedRequests(this.pageIndex, this.filters);
      }
    });
  }

  viewDetailsHandler = (requestId: string, categoryId: string) => {
    const categoryType = this.dictionaryRequestTypes.find(x => x.dictionaryRequestTypeId == categoryId);
    if (categoryType != null) {
      this._router.navigate([categoryType.redirectRoute, requestId]);
    }
  };

  public toggleViewMode(tableViewMode: string) {
    this.tableViewMode = tableViewMode;
    this._globalService.toggleTableViewMode(this.currentRoute, tableViewMode);
  }

  scrollToTop(): void {
    scrollTop();
  }
}
